import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { useAuth } from "../utils/auth";
import { sdkWrapperURL } from "../utils/api-url-list";
import { constants } from "../utils/ranges";

import LoadingScreen from "../components/LoadingScreen";
import ProgressCircle from "../components/ProgressCircle";

import BannerWoman from "../assets/images/BannerWoman.png";
import { ReactComponent as Arrow } from "../assets/icons/Arrow.svg";
import { ReactComponent as Check } from "../assets/icons/Check.svg";
import { ReactComponent as Chevron } from "../assets/icons/Chevron.svg";

const Dashboard = () => {
  const navigate = useNavigate();
  const {
    getToken,
    userData: { employee_id, height, weight },
  } = useAuth();
  const [loading, setLoading] = useState(true);
  const [lastScanResult, setLastResult] = useState({});
  const selectedScoreRange = useMemo(
    () =>
      constants["Health Score"].ranges.reduce((p, c) =>
        lastScanResult.wellness_score > c.point ? c : p
      ),
    [lastScanResult.wellness_score]
  );

  const startScan = () => {
    const bmi = weight / (height / 100) ** 2;
    if (bmi < 10 || bmi > 100) {
      alert(
        `Invalid Height (${height}) or Weight (${weight}) entered. Please provide accurate measurements for correct computation.`
      );
      navigate("/my-account");
    } else navigate("/select-option");
  };

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    (async () => {
      try {
        const token = await getToken();
        const scoreResp = await fetch(
          sdkWrapperURL("/users/sdk/test_api_private"),
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: token,
            },
            body: JSON.stringify({
              action: "list_scan_UL",
              employee_id,
              table_config_rows_per_page: 1,
              table_config_page_no: 1,
            }),
            signal: controller.signal,
          }
        );
        const scoreRespJSON = await scoreResp.json();
        if (
          scoreRespJSON?.statusCode?.toString().startsWith("2") &&
          scoreRespJSON?.result?.length > 0
        )
          setLastResult(scoreRespJSON.result[0]);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    })();
    return () => {
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return loading ? (
    <LoadingScreen style={{ minHeight: "60vh" }} />
  ) : (
    <section className="px-6 py-2">
      <div className="px-6 flex items-center space-x-6">
        <ProgressCircle
          loading={loading}
          value={lastScanResult.wellness_score}
          color={selectedScoreRange.color}
          subtitle={selectedScoreRange.label}
        />
        <div>
          <p className="text-primary text-sm font-semibold">Health Score</p>
          <p className="text-primary/70 text-xxs whitespace-pre-line">
            {lastScanResult.created_at?.length > 0
              ? `Recorded on\n${new Date(
                  lastScanResult.created_at?.replace?.(" ", "T") ?? 0
                ).toLocaleDateString(undefined, {
                  month: "long",
                  day: "numeric",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                  hour12: true,
                })}`
              : "No scan data available"}
          </p>
        </div>
      </div>

      <button
        className="w-full my-6 flex items-center justify-between rounded-xl bg-gradient-to-r from-primary to-[#3545B9] text-white text-left"
        type="button"
        onClick={startScan}
        disabled={loading || lastScanResult.created_at?.length > 0}
      >
        <img
          className="self-end flex-shrink-0 h-20"
          src={BannerWoman}
          alt="Banner"
        />
        <div className="mx-1.5 space-y-1">
          <h3 className="text-xs font-semibold">Know Your Health</h3>
          <p className="text-xxs tracking-tighter leading-tight">
            Get 20+ vital parameters in just 60 seconds.
          </p>
        </div>
        {lastScanResult.created_at?.length > 0 ? (
          <div className="flex-shrink-0 mr-4 flex flex-col items-center justify-center">
            <Check className="flex-shrink-0 h-8 w-8 text-white" />
            <p className="flex-shrink-0 mt-1.5 text-center text-xxs leading-none whitespace-pre-line">
              {"Assessment\nCompleted"}
            </p>
          </div>
        ) : (
          <div className="flex-shrink-0 flex items-center space-x-1 rounded-full mr-3 px-2 py-1 bg-white text-primary">
            <span className="flex-shrink-0 text-xxs tracking-tighter font-medium">
              Start A Scan
            </span>
            <Arrow className="flex-shrink-0 h-2.5 w-2.5" />
          </div>
        )}
      </button>
      {lastScanResult.created_at?.length > 0 && (
        <Link
          to="/scan-result"
          state={{
            result: {
              entry_time: lastScanResult.created_at,
              scan_id: lastScanResult.scan_id,
              posture: lastScanResult.posture,
              wellness_score: lastScanResult.wellness_score,
              vitals: lastScanResult.vitals,
              metadata: {
                heart_scores: lastScanResult.heart_scores,
                cardiovascular: lastScanResult.cardiovascular,
                physiological_scores: lastScanResult.physiological_scores,
                glucose_info: lastScanResult.glucose_info,
              },
            },
          }}
          className="flex items-center justify-between py-4 px-6 rounded-xl bg-[#2B2B2B] text-white"
        >
          <span className="text-sm">View Assessment Result</span>
          <Chevron className="flex-shrink-0 h-4 w-4 -rotate-90" />
        </Link>
      )}
      <div className="my-8">
        <h2 className="mb-4 text-primary text-sm font-semibold">
          About Digital Health Scan
        </h2>
        <iframe
          className="rounded-xl w-full aspect-video bg-black"
          title="Product Video"
          src="https://www.youtube.com/embed/1cbEVfUtyLw"
        />
      </div>
      <div className="my-8">
        <p className="text-darkgray text-xs text-justify">
          Our Digital Health Scan is like having a personal health check-up at
          your fingertips! With just a quick scan, you can monitor important
          vitals like heart rate, blood pressure, and more. Our Vitals
          Monitoring feature makes it easy to stay on top of your well-being, so
          you can feel confident and in control every step of the way.
        </p>
      </div>
    </section>
  );
};

export default Dashboard;
